import {
  Component,
  computed,
  DestroyRef,
  Input,
  OnInit,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { effect } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SHOW_TOOLTIPS_HOME_PAGE } from '@cpd-app/core-module/constants/timer.const';
import { ViaQuestion } from '@cpd-app/core-module/models';
import {
  ConnectDataService,
  ConnectSidebarService,
  NavigationService,
  ViaNudgesService
} from '@cpd-app/core-module/services';
import { AccountCreationPromptService } from '@cpd-app/core-module/services/auth/account-creation-prompt.service';
import { JstSidebarService } from '@cpd-app/core-module/services/jst/jst-sidebar.service';
import { AceSidebarService } from '@cpd-app/core-module/services/ace/ace-sidebar.service';
import { FeatureFlagService } from '@cpd-app/core-module/services/feature-flag/feature-flag.service';
import { ChatViaComponent } from '@cpd-app/features/chat-via/containers/chat-via/chat-via.component';
import { AiChatFacade } from '@cpd-app/state/ai-chat';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { UserProfileResponse } from '@cpd-app/state/user-profile/user-profile.response';
import { firstValueFrom, fromEvent, noop } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  switchMap,
  take
} from 'rxjs/operators';
import { ChatInfoDialogComponent } from '../chat-info-dialog/chat-info-dialog.component';
import { AuthFacade } from '@cpd-app/state/auth';

@Component({
  selector: 'cpd-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  @ViewChild('chatAccessTooltip')
  private readonly chatAccessTooltip: TemplateRef<HTMLDivElement>;

  @ViewChild('connectPromptTooltip')
  private readonly connectPromptTooltip: TemplateRef<HTMLDivElement>;

  @ViewChild('outdatedConnectPromptTooltip')
  private readonly outdatedConnectPromptTooltip: TemplateRef<HTMLDivElement>;

  @ViewChild('connectDisabledTooltip')
  private readonly connectDisabledTooltip: TemplateRef<HTMLDivElement>;

  @ViewChild('jstParsingTooltip')
  private readonly jstParsingTooltip: TemplateRef<HTMLDivElement>;

  @Input() public isChatViaOpen = false;

  /**
   * CPD-671. ONCE PER SESSION, AFTER IT HAS FINISHED STARTING.
   *
   * This waited for `isSessionInitializing` to read false with `take(1)` --
   * and it reads false at boot, before initialisation has begun, so the wait
   * passed instantly and was never a wait. Now each distinct session token
   * waits for the session it belongs to to finish starting, then fires once.
   * A sign-in mints a new token, so a new person still reloads.
   */
  private readonly sessionReady$ = this.authFacade.authToken$.pipe(
    filter(Boolean),
    distinctUntilChanged(),
    switchMap(() =>
      this.authFacade.isSessionInitializing$.pipe(
        filter((isSessionInit) => !isSessionInit),
        take(1)
      )
    ),
    takeUntilDestroyed(this.destroyRef)
  );

  protected readonly showWiggleAnimation = signal(false);
  protected readonly isConnectionOutdated = computed(
    () => this.connectDataService.connectedState().hasOutdatedConnection
  );
  protected readonly matchesCount = computed(
    () => this.connectDataService.matchedConnections().totalCount
  );
  /** Schools waiting to be sent to, counted on the rail. */
  protected readonly connectPendingCount = computed(() =>
    this.connectSidebarService.pendingCount()
  );

  protected readonly isConnectSidebarOpen = computed(() =>
    this.connectSidebarService.isSidebarOpen()
  );
  protected readonly isConnectItemDisabled = toSignal(
    this.connectSidebarService.isConnectItemDisabled$
  );
  protected readonly isJstSidebarOpen = computed(() =>
    this.jstSidebarService.isSidebarOpen()
  );

  // A transcript takes roughly 50 seconds to read and the panel now closes
  // itself while it runs, so this ring on the upload icon is the only thing
  // telling the learner the work is still happening -- and it sits on the
  // control they will come back to.
  protected readonly isJstParsing = computed(() =>
    this.jstSidebarService.isParsing()
  );
  protected readonly isAceSidebarOpen = computed(() =>
    this.aceSidebarService.isSidebarOpen()
  );
  // Reads the backend ACE_TRANSCRIPT_UPLOAD_ENABLED flag. When off (default),
  // the upload entry behaves exactly as today: straight into the JST flow,
  // no doc-type picker. When on, the entry opens the JST-vs-ACE picker.
  protected readonly isAceEnabled = toSignal(
    this.featureFlagService.isEnabled$('ACE_TRANSCRIPT_UPLOAD_ENABLED'),
    { initialValue: false }
  );

  public chatDialogRef: MatDialogRef<ChatViaComponent>;
  public chatInfoDialogRef: MatDialogRef<ChatInfoDialogComponent>;
  public tooltipRef: MatDialogRef<HTMLDivElement>;
  public connectPromptRef: MatDialogRef<HTMLDivElement>;
  public jstParsingRef: MatDialogRef<HTMLDivElement> | null = null;
  public highlight = false;

  constructor(
    private readonly authFacade: AuthFacade,
    private readonly accountCreationPromptService: AccountCreationPromptService,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    private userProfileResponse: UserProfileResponse,
    private cognitoAuthFacade: CognitoAuthFacade,
    private navigationService: NavigationService,
    private aiChatFacade: AiChatFacade,
    private viaNudgesService: ViaNudgesService,
    private connectSidebarService: ConnectSidebarService,
    private connectDataService: ConnectDataService,
    private jstSidebarService: JstSidebarService,
    private aceSidebarService: AceSidebarService,
    private featureFlagService: FeatureFlagService
  ) {}

  /**
   * Show the parsing tag while a transcript is being read, and take it away when
   * it is done.
   *
   * An effect rather than a call inside startUpload(): the parse is owned by
   * JstSidebarService and can begin from more than one place, so driving this off
   * the state means the tag cannot get out of step with it. Closing on completion
   * matters -- the panel reopens on the results at that moment, and a tag still
   * saying "reading your transcript" over the top of them would be wrong.
   *
   * allowSignalWrites: opening or closing a dialog runs MatDialog's
   * afterOpened / afterAllClosed subscribers synchronously, and the home page
   * sets its `anyDialogOpen` signal in one. Inside an effect that write threw
   * NG0600 on every transcript upload from Home and left the flag stale
   * (seen in the 5 Oct UAT review).
   */
  private readonly parsingTagEffect = effect(
    () => {
      const parsing = this.isJstParsing();
      if (parsing && !this.jstParsingRef) {
        this.openParsingTooltip();
      } else if (!parsing && this.jstParsingRef) {
        this.closeParsingTooltip();
      }
    },
    { allowSignalWrites: true }
  );

  private openParsingTooltip(): void {
    if (!this.jstParsingTooltip) {return;}
    this.jstParsingRef = this.dialog.open(this.jstParsingTooltip, {
      hasBackdrop: false,
      // Stays until the parse ends or the learner dismisses it. A stray click
      // must not clear the only indication that work is still running.
      disableClose: true,
      panelClass: 'overflow-visible',
      position: {
        // Aligned to the Upload icon, one nav item below Connect (198px).
        top: '298px',
        left: '20px'
      }
    });
    this.jstParsingRef.afterClosed().subscribe(() => {
      this.jstParsingRef = null;
    });
  }

  public closeParsingTooltip(event?: Event): void {
    event?.stopPropagation();
    this.jstParsingRef?.close();
    this.jstParsingRef = null;
  }

  public ngOnInit(): void {
    this.listenToSkillsSurveyComplete();
    this.listenToOpenChatVia();
    this.listenToTriggerChatTooltip();
    this.listenToTriggerConnectTooltip();
    this.viaNudgesService
      .applyAnimation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.showWiggleAnimation.set(true),
        error: noop
      });

    /*
     * CPD-671. The organisation lists are NOT loaded here any more. Only the
     * Connect panel reads them, and one of them is every school in the
     * country (651 KB), so they load when the panel opens.
     */
    this.sessionReady$.subscribe({
      next: () => {
        // The warning badge on the Connect icon reads
        // `connectedState().hasOutdatedConnection`, and only
        // `getCurrentUserConnections()` ever fills `connectedState`. The rail
        // was calling `getOrganization()` alone, so away from the goals report
        // the badge stayed blank until the member opened Connect -- at which
        // point it was telling them something they had just read inside the
        // panel. A warning that only appears after you have found the problem
        // yourself is not a warning. Same subscription as the organization
        // load, so it costs no extra wiring and cannot get out of step with
        // it.
        this.connectDataService.getCurrentUserConnections().subscribe({
          error: noop
        });
      }
    });
  }

  public toggleChat(): void {
    if (this.isChatViaOpen) {
      this.aiChatFacade.closeChatVia();
      return;
    }

    this.handleOpenChat();
  }

  public toggleConnect(): void {
    if (this.isConnectItemDisabled()) {
      this.openConnectTooltip(this.connectDisabledTooltip);
      return;
    }

    if (this.connectPromptRef) {
      this.closeTooltipConnect();
    }
    this.aiChatFacade.closeChatVia();
    this.jstSidebarService.closeJstSidebar();
    this.aceSidebarService.closeAceSidebar();
    this.connectSidebarService.openConnectSidebar();
  }

  public async toggleJstUpload(): Promise<void> {
    this.aiChatFacade.closeChatVia();
    if (this.isConnectSidebarOpen()) {
      this.connectSidebarService.closeConnectSidebar();
    }

    // The nav rail stays clickable while a panel is open, so a second click is a
    // close -- the same as every other rail button. Both panels are closed here,
    // not just the open one, because opening one on top of the other would leave
    // two overlays stacked with two backdrops, and closing the top one would
    // reveal a stale panel underneath.
    if (this.isJstSidebarOpen() || this.isAceSidebarOpen()) {
      this.jstSidebarService.closeJstSidebar();
      this.aceSidebarService.closeAceSidebar();
      return;
    }

    // A learner who has uploaded before lands on their document list; only a
    // learner with nothing stored goes straight to the doc-type picker. The
    // picker used to open unconditionally whenever the flag was on, which made
    // the document list unreachable from the Upload button -- the one control
    // that exists to reach it -- so an existing upload could not be reviewed and
    // "add another" could not be picked from a list of what was already there.
    const entry = await this.jstSidebarService.resolveUploadEntry(
      this.isAceEnabled()
    );
    if (entry === 'picker') {
      // ACE view state is a root singleton and survives close, so reset before
      // opening or a stale mid-flow view shows instead of the picker.
      this.aceSidebarService.resetFlow();
      await this.aceSidebarService.openAceSidebar();
      return;
    }

    await this.jstSidebarService.openJstSidebar();
  }

  public home(): void {
    if (this.isConnectSidebarOpen()) {
      this.connectSidebarService.closeConnectSidebar();
    }
    if (this.isJstSidebarOpen()) {
      this.jstSidebarService.closeJstSidebar();
    }
    if (this.isAceSidebarOpen()) {
      this.aceSidebarService.closeAceSidebar();
    }

    this.dialog.closeAll();
    this.aiChatFacade.closeChatVia();
    this.navigationService.goToHomePage();
  }

  public openChatInfoDialog(): void {
    if (this.chatInfoDialogRef) {
      return;
    }

    this.chatInfoDialogRef = this.dialog.open(ChatInfoDialogComponent, {
      ariaLabel: 'About Via',
      width: '950px'
    });

    this.chatInfoDialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.chatInfoDialogRef = null;
      });
  }

  public showTooltip(): void {
    this.cognitoAuthFacade.isAuthenticated$
      .pipe(take(1))
      .subscribe((isAuthenticated) => {
        if (this.tooltipRef) {
          this.tooltipRef.close();
        }

        if (!isAuthenticated) {
          this.openTooltip(this.chatAccessTooltip);
        }
      });
  }

  public closeTooltipConnect(event?: Event): void {
    this.connectPromptRef.close();
    this.connectPromptRef = null;
    event?.stopPropagation();
  }

  private handleOpenChat(question?: ViaQuestion): void {
    this.cognitoAuthFacade.isAuthenticated$
      .pipe(take(1))
      .subscribe((isAuthenticated) => {
        this.aiChatFacade.openChatVia();
        if (isAuthenticated) {
          if (question) {
            this.handleSendQuestion(question);
          }
          return;
        }
      });
  }

  private listenToOpenChatVia(): void {
    this.aiChatFacade.askQuestion$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ question }) => {
        if (this.isChatViaOpen) {
          this.handleSendQuestion(question);
          return;
        }

        this.handleOpenChat(question);
      });

    this.aiChatFacade.openChatVia$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.isConnectSidebarOpen()) {
            this.connectSidebarService.closeConnectSidebar();
          }
          if (this.isJstSidebarOpen()) {
            this.jstSidebarService.closeJstSidebar();
          }
          if (this.isAceSidebarOpen()) {
            this.aceSidebarService.closeAceSidebar();
          }
        },
        error: noop
      });
  }

  private listenToSkillsSurveyComplete(): void {
    this.userProfileResponse.isSkillsSurveyComplete$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        delay(SHOW_TOOLTIPS_HOME_PAGE),
        switchMap(() => this.cognitoAuthFacade.isAuthenticated$)
      )
      .subscribe((isAuthenticated) => {
        if (!isAuthenticated) {
          this.openTooltip(this.chatAccessTooltip);
        }
        this.highlight = true;
      });
  }

  private openTooltip(popupContent: TemplateRef<HTMLDivElement>): void {
    const tooltipRef = this.dialog.open(popupContent, {
      ariaLabel: 'Via tip',
      ariaModal: false,
      hasBackdrop: false,
      panelClass: 'overflow-visible',
      position: {
        top: '98px',
        left: '20px'
      }
    });

    tooltipRef.afterOpened().subscribe(() => {
      this.handleCloseTooltip(tooltipRef);
    });

    this.tooltipRef = tooltipRef;
  }

  private handleCloseTooltip(templateRef: MatDialogRef<HTMLDivElement>): void {
    fromEvent(window, 'click')
      .pipe(take(1))
      .subscribe(() => {
        templateRef.close();
        this.highlight = false;
      });
  }

  private openConnectTooltip(popupContent: TemplateRef<HTMLDivElement>): void {
    if (!this.connectPromptRef) {
      this.connectPromptRef = this.dialog.open(popupContent, {
        ariaLabel: 'Connect tip',
        ariaModal: false,
        hasBackdrop: false,
        panelClass: 'overflow-visible',
        position: {
          top: '198px',
          left: '20px'
        }
      });
    }
  }

  private handleSendQuestion(question: ViaQuestion): void {
    if (question && question.query !== 'Ask something else') {
      this.aiChatFacade.streamStart(question.query, question);
    }
  }

  private listenToTriggerChatTooltip(): void {
    this.aiChatFacade.triggerTooltip$
      .pipe(
        // L3: only a guest is told to unlock Via; a member already has it.
        switchMap(() => this.cognitoAuthFacade.isAuthenticated$.pipe(take(1))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (isAuthenticated) => {
          if (!isAuthenticated) {
            this.openTooltip(this.chatAccessTooltip);
          }
        }
      });
  }

  private listenToTriggerConnectTooltip(): void {
    this.connectSidebarService.triggerTooltip$.pipe(take(1)).subscribe({
      next: () => {
        const toolTipRef = this.connectDataService.connectedState()
          .hasOutdatedConnection
          ? this.outdatedConnectPromptTooltip
          : this.connectPromptTooltip;
        this.openConnectTooltip(toolTipRef);
      }
    });
  }
}
